import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { DeliveryService, type MailConversions } from "@services/delivery/deliveryService";

/*
    What the mail actually did (MPN-1706).

    The by-network charts carry mail's spend, because money is comparable across channels.
    Everything else about a postcard is not: a piece is delivered or returned or scanned, and
    none of those has an equivalent on an ad network. They live here rather than being forced
    into impression and click columns where they would mean something else.

    The numbers come from our own database. Lob deletes mailpiece data, tracking and QR scans
    after 90 days, so a report that asked Lob would go blank on any campaign that finished
    last quarter, which is exactly the campaign a client asks about.

    Renders nothing at all when a campaign has never mailed. An empty mail panel on every
    other campaign in the platform is noise.

    The conversion line underneath is the reason each card carries its own QR code. A scan is
    attributable to a person, so the mailed group can be compared against the holdout that was
    deliberately not mailed -- the only comparison that supports saying the mail caused
    anything. "40% of people who scanned converted" is true of every campaign ever run,
    because the people who scan are the people who were already interested.
*/

interface Totals {
  sent: number;
  delivered: number;
  returned: number;
  failed: number;
  scans: number;
  cost: number;
}

export default function DirectMailPanel({ launchId }: { launchId?: number }) {
  const [totals, setTotals] = useState<Totals | null>(null);
  const [outcome, setOutcome] = useState<MailConversions | null>(null);

  useEffect(() => {
    if (!launchId) return;
    let cancelled = false;
    const service = new DeliveryService();
    service
      .launchPerformance(launchId)
      .then((report) => !cancelled && setTotals(report.totals as unknown as Totals))
      .catch(() => !cancelled && setTotals(null));
    // Its own request, and its own failure. A launch with no population segment cannot be
    // measured against a holdout and the server says so with a 400; that is a missing
    // comparison, not a reason to hide how much mail went out.
    service
      .mailConversions(launchId)
      .then((report) => !cancelled && setOutcome(report))
      .catch(() => !cancelled && setOutcome(null));
    return () => {
      cancelled = true;
    };
  }, [launchId]);

  if (!totals || !totals.sent) return null;

  // Of the pieces that have reached an outcome. A card still in the post is not a failure,
  // and counting it as one would make every in-flight campaign look broken.
  const settled = totals.delivered + totals.returned;
  const deliveryRate = settled ? Math.round((totals.delivered / settled) * 100) : null;
  const scanRate = totals.delivered
    ? Math.round((totals.scans / totals.delivered) * 1000) / 10
    : null;

  const figure = (label: string, value: string, note?: string) => (
    <div className="me-4 mb-2">
      <div className="fs-4 fw-semibold">{value}</div>
      <div className="text-muted small">{label}</div>
      {note && <div className="text-muted" style={{ fontSize: 11 }}>{note}</div>}
    </div>
  );

  return (
    <Card className="custom-card mb-0 border border-2">
      <Card.Header>
        <Card.Title>
          <i className="bi bi-envelope me-2" style={{ color: "#5b3df5" }}></i>
          Direct Mail
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-wrap">
          {figure("Pieces sent", totals.sent.toLocaleString())}
          {figure(
            "Delivered",
            totals.delivered.toLocaleString(),
            deliveryRate === null ? "none have landed yet" : `${deliveryRate}% of those settled`,
          )}
          {figure(
            "Returned",
            totals.returned.toLocaleString(),
            totals.returned ? "those addresses will not be mailed again" : undefined,
          )}
          {figure(
            "QR scans",
            totals.scans.toLocaleString(),
            scanRate === null ? undefined : `${scanRate}% of delivered`,
          )}
          {figure("Cost", `$${Number(totals.cost).toFixed(2)}`)}
        </div>
        {totals.failed > 0 && (
          <div className="text-muted small mt-2">
            {totals.failed.toLocaleString()} piece{totals.failed === 1 ? "" : "s"} could not be
            printed, usually an address the postal service rejected.
          </div>
        )}
        {outcome && <Outcome outcome={outcome} />}
        <div className="text-muted mt-3" style={{ fontSize: 11 }}>
          Mail takes days to arrive, so delivery and scans keep climbing after a campaign sends.
        </div>
      </Card.Body>
    </Card>
  );
}

const percent = (rate: number | null) => (rate === null ? "-" : `${(rate * 100).toFixed(1)}%`);

/*
    Conversions among the people who were mailed, cut by whether they scanned.

    Stated as points rather than as a ratio, because a ratio off a small holdout reads as
    "300% better" when it means two people instead of one. No holdout means no claim: the
    groups are still shown, and the lift line simply is not.
*/
function Outcome({ outcome }: { outcome: MailConversions }) {
  const { scanned, not_scanned: quiet, holdout, lift_points: lift } = outcome;
  if (!scanned.people && !quiet.people) return null;

  const row = (label: string, group: typeof scanned, note?: string) => (
    <tr>
      <td className="text-muted">{label}</td>
      <td className="text-end">{group.people.toLocaleString()}</td>
      <td className="text-end">{group.converted.toLocaleString()}</td>
      <td className="text-end fw-semibold">{percent(group.rate)}</td>
      <td className="text-muted ps-3" style={{ fontSize: 11 }}>{note}</td>
    </tr>
  );

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="fw-semibold mb-1">Conversions</div>
      <div className="text-muted mb-2" style={{ fontSize: 11 }}>
        A QR scan is not a conversion. These are real conversions from the client&apos;s own
        records, counted separately for each group.
      </div>
      <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
        <thead>
          <tr className="text-muted" style={{ fontSize: 11 }}>
            <th></th>
            <th className="text-end">People</th>
            <th className="text-end">Converted</th>
            <th className="text-end">Rate</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {row("Mailed, scanned", scanned, "picked the card up and acted on it")}
          {row("Mailed, did not scan", quiet, "may have converted another way")}
          {row("Holdout", holdout, "deliberately not mailed")}
        </tbody>
      </table>
      {lift !== null && (
        <div className="mt-2" style={{ fontSize: 13 }}>
          Mailing lifted conversion by{" "}
          <span className="fw-semibold">{lift > 0 ? "+" : ""}{lift} points</span> against the
          holdout.
        </div>
      )}
      {lift === null && holdout.people === 0 && (
        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
          No holdout on this campaign, so there is nothing to measure the mail against.
        </div>
      )}
    </div>
  );
}
